import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusCircle, Search, Filter, Eye, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { AIProgramCreator } from "@/components/program/ai-program-creator";
import { ProgramTableSkeleton } from "@/components/ui/program-table-skeleton";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export default function PerkPointPrograms() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState("10");
  const [showAICreator, setShowAICreator] = useState(false);

  // Fetch programs from Supabase
  const {
    data: programsData = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["programs"],
    queryFn: async () => {
      console.log("Fetching programs from Supabase...");
      const { data, error } = await supabase
        .from("programs")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching programs:", error);
        throw error;
      }

      console.log("Fetched programs:", data);
      return data || [];
    },
  });

  // Filter the data based on search term and status filter
  const filteredData = programsData.filter((program) => {
    const matchesSearch =
      search === "" ||
      program.name.toLowerCase().includes(search.toLowerCase()) ||
      program.code.toLowerCase().includes(search.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      program.status.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / parseInt(rowsPerPage));
  const paginatedData = filteredData.slice(
    (currentPage - 1) * parseInt(rowsPerPage),
    currentPage * parseInt(rowsPerPage)
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Handle status filter change
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1); // Reset to first page on new filter
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (value: string) => {
    setRowsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Programs</h1>
            <p className="text-muted-foreground">Loading your programs...</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-32">
              <div className="text-muted-foreground">Loading programs...</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Programs</h1>
            <p className="text-muted-foreground">Error loading programs.</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center h-32">
              <div className="text-red-600">
                Error loading programs: {error.message}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Programs</h1>
          <p className="text-muted-foreground">
            Manage your learning programs.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowAICreator(true)}
            className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
          >
            <Zap className="h-4 w-4 mr-2" />
            AI Program Creator
          </Button>
          <Button className="bg-green-600 hover:bg-green-700" asChild>
            <Link to="/perkpoint/create-program">
              <PlusCircle className="mr-2 h-4 w-4" />
              New Program
            </Link>
          </Button>
        </div>
      </div>

      {/* Search and filter controls */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or code..."
            className="pl-10"
            value={search}
            onChange={handleSearchChange}
          />
        </div>
        <div className="w-full md:w-[180px]">
          <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
            <SelectTrigger className="w-full">
              <div className="flex items-center">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Filter by status" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="under review">Under Review</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Programs table */}
      <Card>
        <CardHeader className="bg-gray-50 border-b border-gray-100 py-4 px-6">
          <CardTitle className="text-lg font-medium">Program List</CardTitle>
          <CardDescription>List of all loyalty programs</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50/70">
              <TableRow>
                <TableHead>Program Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Enrolled</TableHead>
                <TableHead>Completion</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <ProgramTableSkeleton />
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    No programs found.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((program) => (
                  <TableRow key={program.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">
                      {program.name}
                    </TableCell>
                    <TableCell>{program.code}</TableCell>
                    <TableCell>
                      {program.city}, {program.country}
                    </TableCell>
                    <TableCell>{program.enrolled_members} members</TableCell>
                    <TableCell>{program.completion_rate}%</TableCell>
                    <TableCell>
                      <div
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          program.status === "Active"
                            ? "bg-green-100 text-green-800"
                            : program.status === "Inactive"
                            ? "bg-gray-100 text-gray-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {program.status}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                        asChild
                      >
                        <Link to={`/perkpoint/program/${program.id}`}>
                          <Eye className="h-4 w-4 mr-1" /> View
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination and rows per page */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <p className="text-sm text-muted-foreground">Rows per page</p>
          <Select value={rowsPerPage} onValueChange={handleRowsPerPageChange}>
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={rowsPerPage} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <EnhancedPaginationControl
          totalPages={totalPages}
          currentPage={currentPage - 1} // Convert to 0-based index
          onPageChange={(page) => setCurrentPage(page + 1)} // Convert back to 1-based index
        />
        <div className="w-[160px]" /> {/* Spacer to balance layout */}
      </div>

      {/* AI Program Creator Dialog */}
      {showAICreator && (
        <AIProgramCreator
          isOpen={showAICreator}
          onClose={() => setShowAICreator(false)}
        />
      )}
    </div>
  );
}
